import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { IconCalendar, IconChevronDown, IconClock } from "@tabler/icons-react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import MDatePicker from "@/components/ui/molecules/MDatePicker";
import MTimePicker, {
  formatTimeString,
} from "@/components/ui/atoms/MTimePicker";
import MTextArea from "@/components/ui/atoms/MTextArea";
import { setNativeKeyboardAvoid } from "@/utils/nativeBridge";
import vitalsTrackingService from "@/services/vitals-tracking.service";
import { VitalsParameter, RecordReadingDto } from "@/types/vitals";
import styles from "./AddHeightModal.module.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface AddHeightModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  currentHeight?: number; // cm — only used in config mode
  mode?: "config" | "reading";
  onSuccess: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cmToFtIn(cm: number): { ft: number; inches: number } {
  const totalInches = cm / 2.54;
  let ft = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches % 12);
  if (inches === 12) {
    ft += 1;
    inches = 0;
  }
  return { ft, inches };
}

function ftInToCm(ft: number, inches: number): number {
  return Math.round((ft * 12 + inches) * 2.54);
}

function combineDateTime(dateStr: string, timeDate: Date): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  const result = new Date(y, m - 1, d);
  result.setHours(timeDate.getHours(), timeDate.getMinutes(), 0, 0);
  return result;
}

// ── Component ─────────────────────────────────────────────────────────────────

const AddHeightModal: React.FC<AddHeightModalProps> = ({
  isOpen,
  onClose,
  memberId,
  currentHeight,
  mode = "config",
  onSuccess,
}) => {
  const { t } = useTranslation();
  const isEdit = mode === "config" && currentHeight != null;

  const [unit, setUnit] = useState<"cm" | "ft">("cm");
  const [cmValue, setCmValue] = useState(() =>
    currentHeight ? String(currentHeight) : ""
  );
  const [ftValue, setFtValue] = useState(() => {
    if (!currentHeight) return "";
    return String(cmToFtIn(currentHeight).ft);
  });
  const [inValue, setInValue] = useState(() => {
    if (!currentHeight) return "";
    return String(cmToFtIn(currentHeight).inches);
  });
  const [recordedAt, setRecordedAt] = useState(() =>
    format(new Date(), "yyyy-MM-dd")
  );
  const [timeDate, setTimeDate] = useState(() => new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const NOTE_MAX = 100;

  useEffect(() => {
    if (!isOpen) return;
    setCmValue(currentHeight ? String(currentHeight) : "");
    if (currentHeight) {
      const { ft, inches } = cmToFtIn(currentHeight);
      setFtValue(String(ft));
      setInValue(String(inches));
    } else {
      setFtValue("");
      setInValue("");
    }
    setRecordedAt(format(new Date(), "yyyy-MM-dd"));
    setTimeDate(new Date());
    setDatePickerOpen(false);
    setTimePickerOpen(false);
    setNoteOpen(false);
    setNote("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const heightCm =
    unit === "cm"
      ? Number(cmValue)
      : ftInToCm(Number(ftValue), Number(inValue));

  const canSave =
    unit === "cm"
      ? !!cmValue && Number(cmValue) > 0
      : !!ftValue && Number(ftValue) > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (mode === "reading") {
        const dto: RecordReadingDto = {
          parameter: VitalsParameter.HEIGHT,
          values: { value: heightCm },
          recordedAt: combineDateTime(recordedAt, timeDate),
          note: note.trim() || undefined,
        };
        await vitalsTrackingService.recordReading(memberId, dto);
      } else {
        await vitalsTrackingService.updateCustomRange(memberId, {
          [VitalsParameter.WEIGHT]: { height: heightCm },
        });
      }
      onSuccess();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === "reading"
      ? t("vitalsTracker.height.readingTitle")
      : isEdit
      ? t("vitalsTracker.height.editTitle")
      : t("vitalsTracker.height.addTitle");

  return (
    <FooterDrawer
      open={isOpen}
      onClose={onClose}
      title={title}
      doneText={saving ? t("common.saving") : t("vitalsTracker.height.save")}
      donePosition="top"
      allowKeyboard
      onDone={handleSave}
      doneDisabled={!canSave || saving}
      height={mode === "reading" ? "85vh" : "80vh"}
    >
      <div className={styles.fields}>
        {mode === "config" && (
          <p className={styles.subtitle}>
            {t("vitalsTracker.height.subtitle")}
          </p>
        )}

        <p className={styles.fieldLabel}>{t("vitalsTracker.height.label")}</p>

        <div className={styles.inputRow}>
          {unit === "cm" ? (
            <>
              <input
                className={styles.input}
                type="number"
                inputMode="numeric"
                placeholder="160"
                value={cmValue}
                onChange={(e) => setCmValue(e.target.value)}
                min={50}
                max={250}
              />
              <span className={styles.unitLabel}>cm</span>
            </>
          ) : (
            <>
              <input
                className={styles.input}
                type="number"
                inputMode="numeric"
                placeholder="5"
                value={ftValue}
                onChange={(e) => setFtValue(e.target.value)}
                min={1}
                max={8}
              />
              <span className={styles.unitLabel}>ft</span>
              <input
                className={`${styles.input} ${styles.inputIn}`}
                type="number"
                inputMode="numeric"
                placeholder="8"
                value={inValue}
                onChange={(e) => setInValue(e.target.value)}
                min={0}
                max={11}
              />
              <span className={styles.unitLabel}>in</span>
            </>
          )}

          {/* cm / ft·in toggle */}
          <div className={styles.unitToggle}>
            <button
              className={`${styles.unitBtn}${
                unit === "cm" ? ` ${styles.unitActive}` : ""
              }`}
              onClick={() => setUnit("cm")}
              type="button"
            >
              cm
            </button>
            <button
              className={`${styles.unitBtn}${
                unit === "ft" ? ` ${styles.unitActive}` : ""
              }`}
              onClick={() => setUnit("ft")}
              type="button"
            >
              ft / in
            </button>
          </div>
        </div>

        {/* When + Note fields — reading mode only */}
        {mode === "reading" && (
          <>
            <p className={styles.fieldLabel}>
              {t("vitalsTracker.addReading.when")}
            </p>
            <div className={styles.whenRow}>
              <button
                type="button"
                className={styles.dateField}
                onClick={() => setDatePickerOpen(true)}
              >
                <span className={styles.dateDisplay}>
                  {format(
                    new Date(
                      ...(recordedAt.split("-").map(Number) as [
                        number,
                        number,
                        number
                      ])
                    ),
                    "d MMM yyyy"
                  )}
                </span>
                <IconCalendar size={18} color="#383838" />
              </button>
              <button
                type="button"
                className={styles.timeField}
                onClick={() => setTimePickerOpen(true)}
              >
                <span className={styles.dateDisplay}>
                  {formatTimeString(timeDate, 12)}
                </span>
                <IconClock size={18} color="#383838" />
              </button>
            </div>
            <MDatePicker
              open={datePickerOpen}
              initialValue={
                recordedAt ? new Date(recordedAt + "T00:00:00") : new Date()
              }
              maxDate={new Date()}
              onDone={(d) => {
                setRecordedAt(format(d, "yyyy-MM-dd"));
                setDatePickerOpen(false);
              }}
              onClose={() => setDatePickerOpen(false)}
            />
            <MTimePicker
              open={timePickerOpen}
              onClose={() => setTimePickerOpen(false)}
              onDone={(d) => {
                const combined = combineDateTime(recordedAt, d);
                const now = new Date();
                setTimeDate(combined > now ? now : d);
                setTimePickerOpen(false);
              }}
              initialValue={timeDate}
              format={12}
            />
            <div>
              <button
                className={styles.accordionTrigger}
                onClick={() => setNoteOpen((p) => !p)}
                type="button"
                aria-expanded={noteOpen}
              >
                <span className={styles.accordionLabel}>
                  {t("vitalsTracker.addReading.note")}
                </span>
                <IconChevronDown
                  size={18}
                  className={`${styles.accordionChevron}${
                    noteOpen ? ` ${styles.open}` : ""
                  }`}
                />
              </button>
              {noteOpen && (
                <div className={styles.accordionBody}>
                  <div className={styles.noteTextareaWrap}>
                    <MTextArea
                      value={note}
                      onChange={(e) => {
                        if (e.target.value.length <= NOTE_MAX)
                          setNote(e.target.value);
                      }}
                      placeholder={t(
                        "vitalsTracker.addReading.notePlaceholder"
                      )}
                      rows={3}
                      onFocus={() => setNativeKeyboardAvoid(true)}
                      onBlur={() => setNativeKeyboardAvoid(false)}
                    />
                  </div>
                  <span className={styles.noteCountBelow}>
                    {NOTE_MAX - note.length} characters
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </FooterDrawer>
  );
};

export default AddHeightModal;
